
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { productService, categoryService } from '../services/api';
import ExportButton from '../components/ExportButton';
import ImportButton from '../components/ImportButton';
import TemplateButton from '../components/TemplateButton';
import GoogleDriveHelp from '../components/GoogleDriveHelp';
import ProductDetailsModal from '../components/ProductDetailsModal';
import { importService } from '../services/importService';
import { normalizeImageUrl, getProductMainImage, handleImageError } from '../utils/imageUtils';
import SmartImage from '../components/SmartImage';
import './Products.css';
import { Typography, Row, Col, Card, Input, Select, Button, Table, Tag, Space, Popconfirm, Switch, Pagination, Dropdown, Tooltip, Flex, App as AntdApp } from 'antd';
import CustomPagination from '../components/CustomPagination';
const { Title, Text } = Typography;

const CLEAN_DISPLAY_REPAIR_MAP = (() => {
  const FFD = '\uFFFD';
  const P = (pieces) => pieces.join(FFD);
  const R = (s, flags) => new RegExp(s, flags);
  const pairs = [
    [P(['D','tect']),       'Détect'],
    [P(['d','tect']),       'détect'],
    [P(['monophas']),       'monophasé'],
    [P(['triphas']),        'triphasé'],
    [P(['r','f','rence']),  'référence'],
    [P(['R','f','rence']),  'Référence'],
    [P(['d','crit']),       'décrit'],
    [P(['','quip']),        'équip'],
    [P(['','tabli']),       'établi'],
    [P(['','tabl']),        'établ'],
    [P(['s','curit']),      'sécurité'],
    [P(['S','curit']),      'Sécurité'],
    [P(['gr','ce']),        'grâce'],
    [P(['','nergie']),      'énergie'],
    [P(['','lectrique']),   'électrique'],
    [P(['','lectron']),     'électron'],
    [P(['fum','e']),        'fumée'],
    [P(['Fum','e']),        'Fumée'],
    [P(['qualit']),         'qualité'],
    [P(['autorit']),        'autorité'],
    [P(['optiqu','e']),     'optique'],
    [P(['Optiqu','e']),     'Optique'],
    [P(['ponctuel']),       'ponctuel'],
    [P(['Ponctuel']),       'Ponctuel'],
    [P(['Gamme']),          'Gamme'],
    [P(['Onduleur']),       'Onduleur'],
    [P(['onduleur']),       'onduleur'],
    [P(['Adressable']),     'Adressable'],
    [P(['adressable']),     'adressable'],
    [P(['parfaite']),       'parfaite'],
    [P(['','quip','s']),    'équipés'],
    [P(['d','tecter']),     'détecter'],
    [P(['d','celer']),      'déceler'],
    [P(['pr','sentes']),    'présentes'],
    [P(['r','flexion']),    'réflexion'],
    [P(['particuli','rement']), 'particulièrement'],
    [P(['d','gagement']),   'dégagement'],
    [P(['compos','']),      'composé'],
    [P(['syst','me']),      'système'],
    [P(['g','n','ralement']),'généralement'],
    [P(['employ','']),      'employé'],
    [P(['','tablissements']),'établissements'],
    [P(['galement']),       'également'],
    [P(['id','al']),        'idéal'],
    [P(['d','tection']),    'détection'],
    [P(['combustion']),     'combustion']
  ];
  return pairs.map(([p, r]) => [R(p.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'), r]);
})();

const cleanDisplay = (v) => {
  if (v === null || v === undefined) return '';
  if (typeof v === 'number' || typeof v === 'boolean') return v;
  if (Array.isArray(v)) return v.map(x => cleanDisplay(x));
  if (typeof v === 'object') {
    const out = {};
    for (const k of Object.keys(v)) out[k] = cleanDisplay(v[k]);
    return out;
  }
  let s = String(v);
  if (/\uFFFD/.test(s)) {
    for (const [rx, replacement] of CLEAN_DISPLAY_REPAIR_MAP) {
      s = s.replace(rx, replacement);
    }
  }
  s = s.replace(/\uFFFD{2,}/g, ' · ');
  s = s.replace(/\uFFFD/g, '');
  s = s.replace(/\s+/g, ' ').trim();
  return s;
};

const INITIAL_FILTERS = {
  search: '',
  status: '',
  category: '',
  isVisible: '',
  brand: '',
  subcategory: '',
  solution: '',
  phase: '',
  puissance: ''
};

const INITIAL_CASCADE = {
  famille: '', // catégorie / famille sélectionnée (nom)
  selections: {}, // { [nomCritere]: valeurChoisie }  (multi-critères tous au sein de LA famille)
};


const FACET_FILTER_CONFIG = [
  { key: 'brand', label: 'Marque', emptyLabel: 'Toutes les marques' },
  { key: 'solution', label: 'Solution', emptyLabel: 'Toutes les solutions' },
  { key: 'phase', label: 'Phase', emptyLabel: 'Toutes les phases' },
  { key: 'puissance', label: 'Puissance', emptyLabel: 'Toutes les puissances' }
];

const Products = () => {
  const { message: messageApi, modal: modalApi } = AntdApp.useApp();
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    ...INITIAL_FILTERS,
    search: searchParams.get('search') || ''
  });
  // ⭐ CASCADE PRO ADMIN LIST - L2/L3
  const [selectedL2Id, setSelectedL2Id] = useState(null);
  const [selectedL3Id, setSelectedL3Id] = useState(null);
  const [selectedProductId, setSelectedProductId] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [query, setQuery] = useState(searchParams.get('search') || '');
  const [categories, setCategories] = useState([]);
  const [facetOptions, setFacetOptions] = useState({});
  const [familyTree, setFamilyTree] = useState([]);   // [{ famille, countProducts, criteres: [{nom, valeurs:[],countValeurs}], ... }]
  const [isFacetLoading, setIsFacetLoading] = useState(false);
  const [cascade, setCascade] = useState(INITIAL_CASCADE);

  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 20
  });

  const abortCtlRef = useRef(null);
  const throttleTimerRef = useRef(null);

  // Synchroniser les paramètres URL avec les filtres
  useEffect(() => {
    const searchFromUrl = searchParams.get('search') || '';
    if (searchFromUrl !== filters.search) {
      setFilters(prev => ({ ...prev, search: searchFromUrl }));
      setQuery(searchFromUrl);
    }
  }, [searchParams]);

  useEffect(() => {
    if (throttleTimerRef.current) {
      clearTimeout(throttleTimerRef.current);
    }
    throttleTimerRef.current = setTimeout(() => {
      loadProducts();
    }, 250);
    return () => {
      if (throttleTimerRef.current) {
        clearTimeout(throttleTimerRef.current);
        throttleTimerRef.current = null;
      }
      if (abortCtlRef.current) {
        try { abortCtlRef.current.abort(); } catch (_) {}
        abortCtlRef.current = null;
      }
    };
  }, [filters, pagination.currentPage, cascade.famille, JSON.stringify(cascade.selections)]);

  useEffect(() => {
    (async () => {
      try {
        const res = await categoryService.getCategories({ includeInactive: true });
        setCategories(res.categories || []);
      } catch (_) {}
    })();
    (async () => {
      try {
        setIsFacetLoading(true);
        const fres = await productService.getProductFacets({});
        setFacetOptions(fres?.facets || {});
        setFamilyTree(Array.isArray(fres?.familyTree) ? fres.familyTree : []);
      } catch (_) { setFacetOptions({}); setFamilyTree([]); }
      finally { setIsFacetLoading(false); }
    })();
  }, []);

  // 🔁 POLLING AUTO 8s Catégories + Facettes + FamilyTree → nouvelles données sans refresh
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const iv = setInterval(async () => {
      try {
        const res = await categoryService.getCategories({ includeInactive: true });
        const next = Array.isArray(res?.categories) ? res.categories : [];
        setCategories(prev => {
          if (!prev || prev.length !== next.length) return next;
          const keyOf = x => String(x.id) + '|' + String(x.name) + '|' + String(x.parentCategoryId || 0) + '|' + (x.updatedAt || '');
          const setP = new Set((prev || []).map(keyOf));
          let same = true;
          for (const n of next) { if (!setP.has(keyOf(n))) { same = false; break; } }
          return same ? prev : next;
        });
        const fres = await productService.getProductFacets({});
        const nf = fres?.facets || {};
        setFacetOptions(prev => {
          const keysP = Object.keys(prev || {});
          const keysN = Object.keys(nf);
          if (keysP.length !== keysN.length) return nf;
          let same = true;
          for (const k of keysN) {
            const a = Array.isArray(prev?.[k]) ? prev[k].join('|') : '';
            const b = Array.isArray(nf?.[k]) ? nf[k].join('|') : '';
            if (a !== b) { same = false; break; }
          }
          return same ? prev : nf;
        });
        const nt = Array.isArray(fres?.familyTree) ? fres.familyTree : [];
        setFamilyTree(prev => {
          if (!prev || prev.length !== nt.length) return nt;
          const keyOf = x => (x?.famille || '') + '::' +
            (x?.criteres || []).map(c => (c.nom || '') + '|' + (c.valeurs || []).join(',')).join(';;');
          const setP = new Set((prev || []).map(keyOf));
          let same = true;
          for (const n of nt) { if (!setP.has(keyOf(n))) { same = false; break; } }
          return same ? prev : nt;
        });
      } catch (_) {}
    }, 8000);
    return () => clearInterval(iv);
  }, []);


  // =========================================================================
  // 🔑 CASCADE STRICTE : Famille (Catégorie) → Critères (seulement ceux de la famille) → Valeurs (seulement du critère)
  // =========================================================================
  const familleOptions = useMemo(() => {
    if (!Array.isArray(familyTree)) return [];
    return familyTree
      .filter(f => f && typeof f.famille === 'string' && f.famille.trim())
      .sort((a, b) => String(a.famille).localeCompare(String(b.famille), 'fr'))
      .map(f => ({
        value: f.famille,
        label: `${f.famille}  (${f.countProducts ?? 0} produits${f.countCriteres ? ` · ${f.countCriteres} critères` : ''})`,
        countProducts: f.countProducts ?? 0,
        countCriteres: f.countCriteres ?? 0
      }));
  }, [familyTree]);

  const familleCourante = useMemo(() => {
    if (!cascade.famille || !Array.isArray(familyTree)) return null;
    return familyTree.find(f => String(f.famille) === String(cascade.famille)) || null;
  }, [familyTree, cascade.famille]);

  const criteresDisponibles = useMemo(() => {
    return familleCourante && Array.isArray(familleCourante.criteres)
      ? familleCourante.criteres.filter(c => c && c.nom && String(c.nom).trim())
      : [];
  }, [familleCourante]);

  const valeursOf = (nomCritere) => {
    const c = criteresDisponibles.find(x => String(x.nom) === String(nomCritere));
    if (!c) return [];
    return Array.isArray(c.valeurs) ? c.valeurs.filter(v => v != null && String(v).trim()) : [];
  };

  const handleCascadeFamilleChange = (familleNom) => {
    // RÈGLE : si famille change → réinitialiser TOUS les critères/valeurs précédents
    if (!familleNom) {
      setCascade(INITIAL_CASCADE);
    } else {
      setCascade({ famille: familleNom, selections: {} });
    }
    setPagination(p => ({ ...p, currentPage: 1 }));
  };

  const handleCascadeCritereChange = (nomCritere, valeur) => {
    setCascade(prev => {
      const next = { ...prev, selections: { ...(prev.selections || {}) } };
      // Si valeur = '' / null / undefined → on supprime le critère des sélections (reset partiel)
      if (!valeur) {
        delete next.selections[nomCritere];
      } else {
        next.selections[nomCritere] = valeur;
      }
      return next;
    });
    setPagination(p => ({ ...p, currentPage: 1 }));
  };

  // 🔽 Helpers CATEGORIE ENFANTS DIRECTS (pour cascade L2/L3)
  const getRawChildren = (parentId) => {
    const pid = typeof parentId === 'object' ? (parentId?.id ?? undefined) : parentId;
    return categories.filter(c => {
      const par = c.parentCategoryId ?? c.parentCategory?.id;
      return String(par ?? '') === String(pid ?? '');
    }).filter(Boolean);
  };
  const selectedCatObj = useMemo(() => {
    if (!filters.category) return null;
    return categories.find(c => String(c.id) === String(filters.category)) || null;
  }, [categories, filters.category]);
  const l2Options = useMemo(() => {
      if (!selectedCatObj) return [];
      return getRawChildren(selectedCatObj.id)
        .filter(c => c && c.name)
        .map(c => ({ value: String(c.id), label: String(c.name), slug: c.slug, id: c.id }));
    }, [selectedCatObj, categories]);
  const l2SelectedObj = useMemo(() => {
      if (!selectedL2Id) return null;
      return categories.find(c => String(c.id) === String(selectedL2Id)) || null;
    }, [categories, selectedL2Id]);
  const l3Options = useMemo(() => {
      if (!l2SelectedObj) return [];
      return getRawChildren(l2SelectedObj.id)
        .filter(c => c && c.name)
        .map(c => ({ value: String(c.id), label: String(c.name), slug: c.slug, id: c.id }));
    }, [l2SelectedObj, categories]);

  const loadProducts = async () => {
    try {
      setIsLoading(true);
      setError(null);

      if (abortCtlRef.current) {
        try { abortCtlRef.current.abort(); } catch (_) {}
      }
      abortCtlRef.current = new AbortController();
      const { signal } = abortCtlRef.current;

      const params = {
        page: pagination.currentPage,
        limit: pagination.itemsPerPage,
        ...filters
      };

      // 🔽 Ajout L2/L3 cascade (subcategory CSV)
      if (selectedL2Id || selectedL3Id) {
        const existing = params.subcategory ? String(params.subcategory).split(',').filter(Boolean) : [];
        if (selectedL2Id && !existing.includes(String(selectedL2Id))) existing.push(String(selectedL2Id));
        if (selectedL3Id && !existing.includes(String(selectedL3Id))) existing.push(String(selectedL3Id));
        if (existing.length > 0) params.subcategory = existing.join(',');
      }

      // 🔑 NOUVEAU : params cascade FAMILLE + CRITÈRES/VALEURS (selon nouvelle architecture)
      if (cascade.famille) {
        params.famille = String(cascade.famille);
        // Sélections multi-critères (toutes dans famille) → fc1_name / fc1_value …
        const sels = Object.entries(cascade.selections || {}).filter(([, v]) => Boolean(v));
        sels.forEach(([nom, valeur], idx) => {
          params[`fc${idx + 1}_name`]  = String(nom);
          params[`fc${idx + 1}_value`] = String(valeur);
        });
      }


      // Nettoyer les filtres vides
      Object.keys(params).forEach(key => {
        if (params[key] === '') {
          delete params[key];
        }
      });

      const response = await productService.getProducts(params, { signal });

      setProducts(response.products);
      setPagination(response.pagination);
    } catch (error) {
      if (error?.name === 'CanceledError' || error?.message === 'canceled') {
        // requête annulée, ne pas afficher d'erreur
        return;
      }
      
      /* console.error('❌ Erreur lors du chargement des produits:', error);
      console.error('❌ Détails de l\'erreur:', {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        config: {
          url: error.config?.url,
          method: error.config?.method
        }
      }); */
      
      let errorMessage = 'Erreur lors du chargement des produits';
      if (error.response) {
        if (error.response.status === 429) {
          errorMessage = 'Trop de requêtes. Veuillez attendre quelques minutes.';
        } else if (error.response.status === 401) {
          errorMessage = 'Session expirée. Veuillez vous reconnecter.';
        } else if (error.response.status === 403) {
          errorMessage = 'Accès refusé. Vérifiez vos permissions.';
        } else if (error.response.status === 500) {
          errorMessage = 'Erreur serveur. Contactez l\'administrateur.';
        } else {
          errorMessage = `Erreur ${error.response.status}: ${error.response.data?.message || error.response.statusText}`;
        }
      } else if (error.request) {
        errorMessage = 'Aucune réponse du serveur. Vérifiez la connexion.';
      } else {
        errorMessage = error.message;
      }
      
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const loadProductFacets = async () => {
    try {
      setIsFacetLoading(true);
      const response = await productService.getProductFacets(
        filters.category ? { category: filters.category } : {}
      );
      setFacetOptions(response?.facets || {});
    } catch (facetError) {
      /* console.error('Erreur lors du chargement des facettes produits:', facetError); */
      setFacetOptions({});
    } finally {
      setIsFacetLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
    setPagination(prev => ({
      ...prev,
      currentPage: 1
    }));
    
    // Mettre à jour l'URL pour le paramètre de recherche
    if (key === 'search') {
      const newSearchParams = new URLSearchParams(searchParams);
      if (value) {
        newSearchParams.set('search', value);
      } else {
        newSearchParams.delete('search');
      }
      setSearchParams(newSearchParams);
    }
  };

  const resetAllFilters = () => {
    setQuery('');
    setFilters(INITIAL_FILTERS);
    setCascade(INITIAL_CASCADE);
    setSelectedL2Id(null);
    setSelectedL3Id(null);
    setPagination(prev => ({
      ...prev,
      currentPage: 1
    }));
    setSearchParams(new URLSearchParams());
  };

  const getFacetSelectOptions = (key) => {
    const values = Array.isArray(facetOptions?.[key]) ? facetOptions[key] : [];
    return values
      .map((value) => String(value || '').trim())
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b, 'fr', { sensitivity: 'base' }))
      .map((value) => ({ value, label: value }));
  };

  const handleDelete = async (productId, productName) => {
    // Confirmation avant suppression
    const confirmed = window.confirm(`Êtes-vous sûr de vouloir supprimer le produit "${productName}" ?`);
    if (!confirmed) return;

    try {
      /* console.log('🔄 Suppression du produit:', productId); */
      const response = await productService.deleteProduct(productId);
      /* console.log('✅ Produit supprimé avec succès:', response); */
      
      // Mettre à jour la liste des produits
      setProducts(products.filter(p => p.id !== productId));
      
      // Afficher un message de succès
      messageApi.success('Produit supprimé avec succès');
      
    } catch (error) {
      /* console.error('❌ Erreur lors de la suppression:', error); */
      
      let errorMessage = 'Erreur lors de la suppression du produit';
      if (error.response) {
        errorMessage = `Erreur ${error.response.status}: ${error.response.data?.message || error.response.statusText}`;
      } else if (error.request) {
        errorMessage = 'Aucune réponse du serveur';
      } else {
        errorMessage = error.message;
      }
      
      messageApi.error(errorMessage);
    }
  };

  const toggleVisibility = async (product) => {
    try {
      const updated = await productService.toggleVisibility(product.id);
      setProducts(products.map(p => p.id === product.id ? { ...p, isVisible: updated.product.isVisible, status: updated.product.status } : p));
    } catch (error) {
      /* console.error('Erreur lors de la mise à jour de visibilité:', error); */
      alert('Erreur lors de la mise à jour de la visibilité');
    }
  };

  const handleViewProduct = (productId) => {
    setSelectedProductId(productId);
    setIsModalVisible(true);
  };


  const handleCloseModal = () => {
    setIsModalVisible(false);
    setSelectedProductId(null);
  };

  const handleTopExport = async (format, scope) => {
    try {
      const { exportService } = await import('../services/exportService');
      if (scope === 'all') {
        if (format === 'excel') {
          await exportService.exportProductsToExcel(filters);
        } else if (format === 'pdf') {
          await exportService.exportProductsToPDF(filters);
        }
      } else if (scope === 'selected') {
        if (selectedRowKeys.length === 0) {
          alert('Sélectionnez au moins un produit.');
          return;
        }
        if (selectedRowKeys.length === 1) {
          const productId = selectedRowKeys[0];
          if (format === 'excel') {
            await exportService.exportSingleProductToExcel(productId);
          } else if (format === 'pdf') {
            await exportService.exportSingleProductToPDF(productId);
          }
        } else {
          const enhancedFilters = { ...filters, selectedIds: selectedRowKeys };
          if (format === 'excel') {
            await exportService.exportProductsToExcel(enhancedFilters);
          } else if (format === 'pdf') {
            await exportService.exportProductsToPDF(enhancedFilters);
          }
        }
      }
    } catch (error) {
      /* console.error('Erreur lors de l\'export:', error); */
      alert('Erreur lors de l\'export');
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      draft: { label: 'Brouillon', class: 'badge--warning' },
      published: { label: 'Publié', class: 'badge--success' },
      archived: { label: 'Archivé', class: 'badge--secondary' }
    };
    
    const statusInfo = statusMap[status] || { label: status, class: 'badge--secondary' };
    
    return (
      <span className={`badge ${statusInfo.class}`}>
        {statusInfo.label}
      </span>
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  if (isLoading) {
    return (
      <div className="products-page">
        <div className="loading-container">
          <i className="fas fa-spinner fa-spin"></i>
          <p>Chargement des produits...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="products-page">
      <div className="page-header">
        <Flex justify="space-between" align="center" wrap="wrap" gap={12} style={{ width: '100%' }}>
          <Title level={3} style={{ margin: 0 }}>Gestion des Produits</Title>
          <Flex wrap="wrap" gap={10}>
            <Dropdown
              menu={{
                items: [
                  {
                    key: 'all-excel',
                    label: `Exporter tous (${pagination?.totalItems ? ` ${pagination.totalItems}` : ''})`,
                    icon: <i className="fas fa-file-excel"></i>,
                    onClick: () => handleTopExport('excel', 'all')
                  },
                  { type: 'divider' },
                  {
                    key: 'selected-excel',
                    label: `Exporter la sélection (${` ${selectedRowKeys.length}`  })`,
                    icon: <i className="fas fa-file-excel"></i>,
                    disabled: selectedRowKeys.length === 0,
                    onClick: () => handleTopExport('excel', 'selected')
                  }
                ]
              }}
              trigger={['click']}
            >
              <Button size="large" className="template-button--info" title="Exporter Produits" style={{ backgroundColor: 'rgb(84, 186, 102)', color: '#fff', padding: '8px 20px', fontSize: 16, height: 44 }}>
                {`Exporter Produits`}
              </Button>
            </Dropdown>
            <TemplateButton
              label="Template Import"
              icon="fas fa-download"
              className="template-button--info"
              onSuccess={(message) => {
                /* console.log('Template téléchargé:', message); */
              }}
              onError={(error) => {
                alert(`Erreur téléchargement template: ${error}`);
              }}
            />
            <ImportButton
              type="products"
              className="import-button--primary"
              icon="fas fa-file-import"
              onSuccess={(result) => {
                /* console.log('Import réussi:', result); */
                setFilters(INITIAL_FILTERS);
                setQuery('');
                setPagination(prev => ({ ...prev, currentPage: 1 }));
                loadProducts();
              }}
              onError={(error) => {
                alert(`Erreur d'import: ${error}`);
              }}
            />
            <Button type="primary" style={{backgroundColor:'#002c3e'}}>
              <Link to="/admin/products/new" style={{ color: '#fff' }}><i className="fas fa-plus"></i> Nouveau produit</Link>
            </Button>
          </Flex>
        </Flex>
      </div>

      {error && (
        <div className="alert alert--danger">
          <i className="fas fa-exclamation-triangle alert__icon"></i>
          <div className="alert__content">
            <div className="alert__title">Erreur</div>
            <div className="alert__message">{error}</div>
          </div>
        </div>
      )}


      <Card style={{ marginBottom: 16 }}>
        {/* Aide Google Drive */}
        <GoogleDriveHelp />
        
        <Row gutter={[12, 12]}>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Text strong>Rechercher</Text>
            <Input
              placeholder="Nom, référence..."
              value={query}
              allowClear
              onChange={(e) => {
                setQuery(e.target.value);
                handleFilterChange('search', e.target.value);
              }}
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Text strong>Statut</Text>
            <Select
              value={filters.status || ''}
              allowClear
              onChange={(v) => handleFilterChange('status', v)}
              style={{ width: '100%' }}
              options={[
                { value: '', label: 'Tous les statuts' },
                { value: 'draft', label: 'Brouillon' },
                { value: 'published', label: 'Publié' },
                { value: 'archived', label: 'Archivé' }
              ]}
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Text strong>Visibilité</Text>
            <Select
              value={filters.isVisible || ''}
              allowClear
              onChange={(v) => handleFilterChange('isVisible', v)}
              style={{ width: '100%' }}
              options={[
                { value: '', label: 'Tous' },
                { value: 'true', label: 'Visible' },
                { value: 'false', label: 'Masqué' }
              ]}
            />
          </Col>

          {/* ===================================================================== */}
          {/* 🔑 NOUVEAU : FILTRE CASCADE STRICTE Catégorie → Critères → Valeurs  */}
          {familleOptions.length > 0 && (
            <>
              <Col xs={24}>
                <div style={{ margin: '14px 2px 6px 2px', padding: '8px 10px', borderRadius: 8, background: 'linear-gradient(90deg,#ecfeff,#ecfdf5)', border: '1px dashed #0d9488', fontSize: 13, color: '#0f766e', fontWeight: 600 }}>
                  <i className="fas fa-filter"></i> Filtres &nbsp;→&nbsp; <span style={{ color: '#002c3e' }}>Catégorie</span> &nbsp;→&nbsp; <span style={{ color: '#0284c7' }}>Critères de la catégorie</span> &nbsp;→&nbsp; <span style={{ color: '#0ea5e9' }}>Valeurs du critère</span> &nbsp;→&nbsp; Produits
                </div>
              </Col>

              {/* ÉTAPE 1 : Catégorie (Famille) */}
              <Col xs={24} sm={12} md={12} lg={8}>
                <Text strong style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ background: '#002c3e', color: '#fff', padding: '2px 10px', borderRadius: 999, fontSize: 11, fontWeight: 700 }}>1</span>
                  Choisir une Catégorie
                </Text>
                <Select
                  value={cascade.famille || undefined}
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  loading={isFacetLoading}
                  onChange={handleCascadeFamilleChange}
                  style={{ width: '100%' }}
                  size="large"
                  placeholder="Toutes les catégories..."
                  options={[
                    { value: '', label: '— Toutes les catégories (sans filtres) —' },
                    ...familleOptions
                  ]}
                />
              </Col>

              {/* ÉTAPE 2 + 3 : CRITÈRES (uniquement ceux de la famille sélectionnée) + VALEURS associées */}
              {cascade.famille && (
                criteresDisponibles.length === 0 ? (
                  <Col xs={24}>
                    <div style={{ padding: 14, borderRadius: 8, background: '#fffbeb', color: '#92400e', border: '1px dashed #f59e0b', fontSize: 13 }}>
                      <i className="fas fa-exclamation-triangle"></i> Aucun critère défini pour
                      &nbsp;<b>{cascade.famille}</b>.&nbsp;
                      Ajoutez des colonnes <code>Critère N / Valeur Critère N</code> lors de l'import Excel
                      ou remplissez <code>facets._familleCriteres</code>.
                    </div>
                  </Col>
                ) : (
                  criteresDisponibles.map((crit, i) => {
                    const valeurs = valeursOf(crit.nom);
                    const selected = cascade.selections?.[crit.nom] || '';
                    return (
                      <Col xs={24} sm={12} md={12} lg={8} key={String(crit.nom)}>
                        <Text strong style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span style={{ background: '#0284c7', color: '#fff', padding: '2px 10px', borderRadius: 999, fontSize: 11, fontWeight: 700 }}>{i + 1}</span>
                          Critère — {String(crit.nom)}
                          <Text type="secondary" style={{ marginLeft: 'auto', fontSize: 11, fontWeight: 400 }}>
                            {valeurs.length} valeur{valeurs.length > 1 ? 's' : ''}
                          </Text>
                        </Text>
                        <Select
                          allowClear
                          showSearch
                          optionFilterProp="label"
                          placeholder={`Valeurs pour "${String(crit.nom)}"...`}
                          value={selected || undefined}
                          onChange={(v) => handleCascadeCritereChange(String(crit.nom), v || '')}
                          style={{ width: '100%' }}
                          size="large"
                          options={[
                            { value: '', label: `— Toutes les valeurs de "${String(crit.nom)}" —` },
                            ...valeurs.map((v) => ({ value: v, label: v }))
                          ]}
                        />
                      </Col>
                    );
                  })
                )
              )}
            </>
          )}
          {/* ============ FIN DU NOUVEAU BLOC CASCADE ============================ */}
          <Col xs={24} sm={12} md={8} lg={6}>
            <Text strong><span style={{ color: '#0369a1' }}>1.</span> Catégorie</Text>
            <Select
              value={filters.category || ''}
              allowClear
              onChange={(v) => handleFilterChange('category', v)}
              style={{ width: '100%' }}
              showSearch
              optionFilterProp="label"
              options={[{ value: '', label: 'Toutes les catégories' }, ...categories.filter(c => !(c.parentCategoryId || c.parentCategory?.id)).map(c => ({ value: c.id, label: c.name }))]}
            />
          </Col>

          {/* 🏷️ CASCADE PRO - NIVEAU 2 (SOUS-CATÉGORIE) - SEULEMENT si catégorie choisie ET enfants existants */}
          {selectedCatObj && l2Options.length > 0 && (
            <Col xs={24} sm={12} md={8} lg={6}>
              <Text strong><span style={{ color: '#0284c7' }}>2.</span> Sous-catégorie</Text>
              <Select
                value={selectedL2Id || ''}
                allowClear
                showSearch
                optionFilterProp="label"
                onChange={(v) => {
                  setSelectedL2Id(v || null);
                  setSelectedL3Id(null);
                  handleFilterChange('subcategory', v || '');
                }}
                style={{ width: '100%' }}
                options={[{ value: '', label: `Sous-catégories : ${selectedCatObj.name}` }, ...l2Options]}
              />
            </Col>
          )}

          {/* 🏷️ CASCADE PRO - NIVEAU 3 (SOUS-SOUS-CATÉGORIE) - SEULEMENT si L2 choisie ET petits-enfants existants */}
          {selectedL2Id && l2SelectedObj && l3Options.length > 0 && (
            <Col xs={24} sm={12} md={8} lg={6}>
              <Text strong><span style={{ color: '#0891b2' }}>3.</span> {l2SelectedObj?.name || 'Suite'}</Text>
              <Select
                value={selectedL3Id || ''}
                allowClear
                showSearch
                optionFilterProp="label"
                onChange={(v) => {
                  setSelectedL3Id(v || null);
                  handleFilterChange('subcategory', v || (selectedL2Id ? String(selectedL2Id) : ''));
                }}
                style={{ width: '100%' }}
                options={[{ value: '', label: 'Choisir...' }, ...l3Options]}
              />
            </Col>
          )}

          {FACET_FILTER_CONFIG.map((facet) => (
            <Col xs={24} sm={12} md={8} lg={6} key={facet.key}>
              <Text strong>{facet.label}</Text>
              <Select
                value={filters[facet.key] || ''}
                onChange={(value) => handleFilterChange(facet.key, value)}
                style={{ width: '100%' }}
                showSearch
                allowClear
                loading={isFacetLoading}
                optionFilterProp="label"
                options={[
                  { value: '', label: facet.emptyLabel },
                  ...getFacetSelectOptions(facet.key)
                ]}
              />
            </Col>
          ))}
          <Col xs={24}>
            <Flex justify="flex-end" wrap="wrap" gap={10} style={{ width: '100%' }}>
              <Button onClick={resetAllFilters}>
                Réinitialiser les filtres
              </Button>
            </Flex>
          </Col>
        </Row>
      </Card>

      <div className="products-section">
        <Flex justify="space-between" align="center" wrap="wrap" gap={12}>
          <h2 style={{ margin: 0 }}>
            Produits ({pagination.totalItems})
          </h2>
          <Dropdown
            menu={{
              items: [
                {
                  key: 'selected',
                  label: `Supprimer la sélection (${selectedRowKeys.length})`,
                  disabled: selectedRowKeys.length === 0,
                  onClick: async () => {
                    modalApi.confirm({
                      title: `Supprimer ${selectedRowKeys.length} produit(s) sélectionné(s) ?`,
                      okText: 'Oui',
                      cancelText: 'Non',
                      okButtonProps: { danger: true },
                      onOk: async () => {
                        try {
                          if (selectedRowKeys.length === 0) return;
                          await productService.bulkDeleteProducts(selectedRowKeys);
                          setSelectedRowKeys([]);
                          loadProducts();
                        } catch (e) {
                          modalApi.error({ title: 'Erreur', content: 'Erreur suppression en masse' });
                        }
                      }
                    });
                  }
                },
                {
                  type: 'divider'
                },
                {
                  key: 'all',
                  danger: true,
                  label: 'Supprimer TOUT le tableau',
                  onClick: async () => {
                    modalApi.confirm({
                      title: 'Supprimer TOUT le tableau de produits ?',
                      content: 'Cette action est irréversible et supprimera tous les produits de la base.',
                      okText: 'Oui, supprimer tout',
                      cancelText: 'Annuler',
                      okButtonProps: { danger: true },
                      onOk: async () => {
                        try {
                          /* console.log('🔄 Suppression totale de tous les produits...'); */
                          const response = await productService.bulkDeleteAllProducts();
                          /* console.log('✅ Suppression totale réussie:', response); */
                          setSelectedRowKeys([]);
                          loadProducts();
                          messageApi.success(`Suppression totale réussie: ${response.deleted || 0} produits supprimés`);
                        } catch (error) {
                          /* console.error('❌ Erreur suppression totale:', error); */
                          let errorMessage = 'Erreur suppression totale';
                          if (error.response) {
                            errorMessage = `Erreur ${error.response.status}: ${error.response.data?.message || error.response.statusText}`;
                          } else if (error.request) {
                            errorMessage = 'Aucune réponse du serveur';
                          } else {
                            errorMessage = error.message;
                          }
                          modalApi.error({
                            title: 'Erreur suppression totale',
                            content: errorMessage,
                            width: 600
                          });
                        }
                      }
                    });
                  }
                },
              ]
            }}
          >
            <Button danger type="primary">
              <i className="fas fa-trash"></i> Supprimer
            </Button>
          </Dropdown>
        </Flex>

        {products.length === 0 ? (
          <div className="empty-state">
            <i className="fas fa-box"></i>
            <h3>Aucun produit trouvé</h3>
          </div>
        ) : (
          <>
            <div style={{ width: '100%', overflowX: 'auto', marginTop: 16 }}>
              <Table
                rowKey="id"
                dataSource={products}
                pagination={false}
                scroll={{ x: 'max-content' }}
                rowSelection={{
                  selectedRowKeys,
                  onChange: (keys) => setSelectedRowKeys(keys),
                }}
                onChange={(pag, tableFilters) => {
                  if (Array.isArray(tableFilters.status)) handleFilterChange('status', tableFilters.status[0] || '');
                  if (Array.isArray(tableFilters.isVisible)) handleFilterChange('isVisible', tableFilters.isVisible[0] ?? '');
                  if (Array.isArray(tableFilters.categories)) handleFilterChange('category', tableFilters.categories[0] || '');
                }}
                columns={[
                  {
                    title: 'Image',
                    dataIndex: ['images', 'main'],
                    width: 100,
                    render: (value, product) => (
                      <SmartImage
                        src={getProductMainImage(product)}
                        alt={cleanDisplay(product.name) || 'Produit'}
                        width={60}
                        height={60}
                        style={{ objectFit: 'cover' }}
                      />
                    )
                  },
                  {
                    title: 'Produit',
                    dataIndex: 'name',
                    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
                      <div style={{ padding: 8 }}>
                        <Input
                          placeholder="Nom..."
                          value={selectedKeys[0]}
                          onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                          onPressEnter={() => { setQuery(selectedKeys[0] || ''); handleFilterChange('search', selectedKeys[0] || ''); confirm(); }}
                          style={{ width: 180, marginBottom: 8, display: 'block' }}
                        />
                        <Space>
                          <Button type="primary" size="small" onClick={() => { setQuery(selectedKeys[0] || ''); handleFilterChange('search', selectedKeys[0] || ''); confirm(); }}>OK</Button>
                          <Button size="small" onClick={() => { setSelectedKeys([]); setQuery(''); handleFilterChange('search', ''); clearFilters && clearFilters(); confirm(); }}>Reset</Button>
                        </Space>
                      </div>
                    ),
                    filterIcon: (filtered) => <i className="fas fa-search" style={{ color: filtered ? '#10b981' : undefined }} />,
                    render: (text, product) => (
                      <div className="product-info">
                        <strong>{cleanDisplay(product.name)}</strong>
                        <div>{cleanDisplay(product.shortDescription) || 'Aucune description'}</div>
                      </div>
                    )
                  },
                  {
                    title: 'Référence',
                    dataIndex: 'reference',
                    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
                      <div style={{ padding: 8 }}>
                        <Input
                          placeholder="Référence..."
                          value={selectedKeys[0]}
                          onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                          onPressEnter={() => { setQuery(selectedKeys[0] || ''); handleFilterChange('search', selectedKeys[0] || ''); confirm(); }}
                          style={{ width: 160, marginBottom: 8, display: 'block' }}
                        />
                        <Space>
                          <Button type="primary" size="small" onClick={() => { setQuery(selectedKeys[0] || ''); handleFilterChange('search', selectedKeys[0] || ''); confirm(); }}>OK</Button>
                          <Button size="small" onClick={() => { setSelectedKeys([]); setQuery(''); handleFilterChange('search', ''); clearFilters && clearFilters(); confirm(); }}>Reset</Button>
                        </Space>
                      </div>
                    ),
                    filterIcon: (filtered) => <i className="fas fa-search" style={{ color: filtered ? '#10b981' : undefined }} />,
                    render: (t) => <code>{t}</code>
                  },
                  {
                    title: 'Catégories',
                    dataIndex: 'categories',
                    filters: categories.map((c) => ({ text: c.name, value: c.id })),
                    filterMultiple: false,
                    filterSearch: true,
                    render: (cats = []) => (
                      <Space size={[4, 4]} wrap>
                        {cats.slice(0, 2).map((c) => <Tag key={c.id}>{c.name}</Tag>)}
                        {cats.length > 2 && <Tag>+{cats.length - 2}</Tag>}
                      </Space>
                    )
                  },
                  {
                    title: 'Statut',
                    dataIndex: 'status',
                    filters: [
                      { text: 'Publié', value: 'published' },
                      { text: 'Brouillon', value: 'draft' },
                      { text: 'Archivé', value: 'archived' },
                    ],
                    filterMultiple: false,
                    render: (status) => (
                      <Tag color={status === 'published' ? 'green' : status === 'draft' ? 'orange' : 'default'}>
                        {status === 'published' ? 'Publié' : status === 'draft' ? 'Brouillon' : 'Archivé'}
                      </Tag>
                    )
                  },
                  {
                    title: 'Visibilité',
                    dataIndex: 'isVisible',
                    filters: [
                      { text: 'Visible', value: 'true' },
                      { text: 'Masqué', value: 'false' },
                    ],
                    filterMultiple: false,
                    render: (v, product) => (
                      <Switch checked={product.isVisible} onChange={() => toggleVisibility(product)} />
                    )
                  },
                  {
                    title: 'Date',
                    dataIndex: 'createdAt',
                    sorter: true,
                    render: (d) => <span className="date-text">{formatDate(d)}</span>
                  },
                  {
                    title: 'Actions',
                    fixed: 'right',
                    width: 190,
                    render: (_, product) => (
                      <Space size={8}>
                        <Tooltip title="Voir les détails">
                          <Button
                            type="default"
                            shape="circle"
                            icon={<i className="fas fa-eye"></i>}
                            onClick={() => handleViewProduct(product.id)}
                          />
                        </Tooltip>
                        <Tooltip title="Modifier">
                          <Link to={`/admin/products/${product.id}/edit`}>
                            <Button type="primary" shape="circle" icon={<i className="fas fa-edit"></i>} />
                          </Link>
                        </Tooltip>
                        <Tooltip title="Supprimer">
                          <Popconfirm
                            title={`Supprimer "${product.name}" ?`}
                            description="Cette action est irréversible."
                            okText="Oui"
                            cancelText="Non"
                            okButtonProps={{ danger: true }}
                            onConfirm={() => handleDelete(product.id, product.name)}
                          >
                            <Button danger shape="circle" icon={<i className="fas fa-trash"></i>} />
                          </Popconfirm>
                        </Tooltip>
                      </Space>
                    )
                  },
                ]}
              />
            </div>

            {pagination.totalPages > 1 && (
              <Flex justify="center" wrap style={{ marginTop: 24, width: '100%' }}>
                <CustomPagination
                  current={pagination.currentPage}
                  total={pagination.totalItems}
                  pageSize={pagination.itemsPerPage}
                  onChange={(page, size) => setPagination(prev => ({
                    ...prev,
                    currentPage: page,
                    itemsPerPage: size ?? prev.itemsPerPage
                  }))}
                  showSizeChanger={true}
                  showQuickJumper={true}
                  showTotal={true}
                  size="default"
                />
              </Flex>
            )}
          </>
        )}
      </div>

      <ProductDetailsModal
        visible={isModalVisible}
        onClose={handleCloseModal}
        productId={selectedProductId}
      />
    </div>
  );
};

export default Products;
